import React, { useState } from 'react';

import InstallPWAButton from '@/components/client-hub/InstallPWAButton';
import ClientHubContactWebform from '@/components/client-hub/sections/contact-webform';
import convertTimeFormat from '@/helper/convertTimeFormat';
import chevronDown from '@/public/images/icons/chevron-down.svg';
import { BRAND_TITLE } from '@/utils/constants';

const siteUrl = process.env.NEXT_PUBLIC_SITE_URL_ORIGIN;

const ClientHubContact = (props: {
  isMobile: boolean;
  activeSection: string;
  track: Function;
  facilityData: any;
  local: 'en' | 'fr';
}) => {
  const { isMobile, activeSection, track, facilityData, local } = props;

  const id = 'contact';
  const Address = facilityData?.Address;

  const [expandOfficeHours, setExpandOfficeHours] = useState(false);
  const officeHours = (
    dayOfWeek:
      | 'Monday'
      | 'Tuesday'
      | 'Wednesday'
      | 'Thursday'
      | 'Friday'
      | 'Saturday'
      | 'Sunday'
  ) => {
    return convertTimeFormat(facilityData.Store_Hours[dayOfWeek], local);
  };

  const [expandGateHours, setExpandGateHours] = useState(false);
  const gateHours = (
    dayOfWeek:
      | 'Monday'
      | 'Tuesday'
      | 'Wednesday'
      | 'Thursday'
      | 'Friday'
      | 'Saturday'
      | 'Sunday'
  ) => {
    return convertTimeFormat(facilityData.Open_Hours[dayOfWeek], local);
  };

  const showSection = () => {
    if (isMobile) return activeSection === id;
    return true;
  };

  const [showWebForm, setShowWebForm] = useState(false);
  const toggleWebForm = () => {
    if (!showWebForm) track('Expand contact web form');
    setShowWebForm(!showWebForm);
  };

  if (!facilityData) return null;

  const t = {
    welcome:
      local === 'fr' ? (
        <>
          C’est un plaisir de vous accueillir à{' '}
          <b>
            {BRAND_TITLE.FR} – {facilityData?.Title || ''}
          </b>
          . C’est ici que vous trouverez tout ce dont vous avez besoin pour{' '}
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          commencer votre expérience d'entreposage.
        </>
      ) : (
        `We are excited to welcome you to ${BRAND_TITLE.EN} – ${
          facilityData?.Title || ''
        }. Here you can find everything you need to get started.`
      ),

    contactUs: local === 'fr' ? 'Nous joindre' : 'Contact us',
    supportText:
      local === 'fr'
        ? 'Nous sommes là pour vous accompagner à chaque étape.'
        : 'We are here to support you at every step.',

    byPhone: local === 'fr' ? 'Par téléphone' : 'By phone',
    byWebForm: local === 'fr' ? 'Par formulaire Web' : 'By web form',
    address: local === 'fr' ? 'Adresse' : 'Address',
    website: local === 'fr' ? 'Site Web' : 'Website',
    officeHours:
      local === 'fr' ? 'Heures d’ouverture du bureau' : 'Office hours',
    gateHours:
      local === 'fr' ? "Heures d'accès à l’installation" : 'Gate hours',
  };

  return (
    <div style={{ display: showSection() ? 'block' : 'none' }}>
      <div style={{ marginBottom: '30px' }}>{t.welcome}</div>

      {isMobile && (
        <div style={{ marginBottom: '30px' }}>
          <div onClick={() => track('Install app')}>
            <InstallPWAButton local={local} />
          </div>
        </div>
      )}

      <h3
        id="contact"
        style={{ textTransform: 'none', backgroundColor: 'aliceblue' }}
      >
        {t.contactUs}
      </h3>

      <div>
        {t.supportText}
        <br />
        <br />

        <div>
          <b>
            {t.byPhone}:{' '}
            <a
              style={{ textDecoration: 'none' }}
              onClick={() => track('Call store')}
              href={`tel:${facilityData?.Support?.Phone_Number}`}
            >
              {facilityData?.Support?.Phone_Number}
            </a>
          </b>
        </div>

        <div onClick={toggleWebForm} style={{ padding: '20px 0 10px 0' }}>
          <b>
            {t.byWebForm}{' '}
            <a
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                return false;
              }}
              href="#"
            ></a>
            <img
              src={chevronDown.src}
              style={{
                transform: showWebForm ? 'rotateX(180deg)' : '',
                filter:
                  'invert(44%) sepia(71%) saturate(5305%) hue-rotate(183deg) brightness(99%) contrast(101%)',
              }}
              alt="chevron-down"
            />
          </b>
        </div>

        {showWebForm && (
          <div>
            <ClientHubContactWebform
              track={track}
              facilityId={facilityData?.Facility_ID}
              local={local}
            />
          </div>
        )}

        <hr />
        <div>
          <span>
            <b>{t.address}:</b>{' '}
            <a
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
              onClick={() => track('Open store directions')}
              href={`https://www.google.com/maps/search/?api=1&query=%20&query_place_id=${facilityData?.Google_PlaceID}`}
            >
              {Address?.Street_1} {Address?.Street_2}, {Address?.City}{' '}
              {Address?.Postal_Code}
            </a>
          </span>
          <br />

          <div style={{ paddingTop: '10px' }}>
            <b>{t.website}:</b>{' '}
            <a
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
              onClick={() => track('Open store page')}
              href={`${siteUrl}/${local}/self-storage/${
                facilityData?.City?.data?.attributes?.Slug
                  ? `${facilityData?.City?.data?.attributes?.Slug?.replace(
                      '-fr',
                      ''
                    )}/`
                  : ''
              }${facilityData?.Slug?.replace('-fr', '')}/`}
            >
              {local === 'en' ? BRAND_TITLE.EN : BRAND_TITLE.FR} –{' '}
              {facilityData?.Title || ''}
            </a>{' '}
            <br />
          </div>

          <div
            onClick={() => setExpandOfficeHours(!expandOfficeHours)}
            style={{ paddingTop: '10px' }}
          >
            <b>
              {t.officeHours}{' '}
              <img
                src={chevronDown.src}
                style={{
                  transform: expandOfficeHours ? 'rotateX(180deg)' : '',
                }}
                alt="chevron-down"
              />{' '}
            </b>
          </div>
          <div
            style={{
              display: expandOfficeHours ? 'block' : 'none',
              paddingLeft: '10px',
            }}
          >
            <div>
              {local === 'fr' ? 'Lundi' : 'Monday'}: {officeHours('Monday')}
            </div>
            <div>
              {local === 'fr' ? 'Mardi' : 'Tuesday'}: {officeHours('Tuesday')}
            </div>
            <div>
              {local === 'fr' ? 'Mercredi' : 'Wednesday'}:{' '}
              {officeHours('Wednesday')}
            </div>
            <div>
              {local === 'fr' ? 'Jeudi' : 'Thursday'}: {officeHours('Thursday')}
            </div>
            <div>
              {local === 'fr' ? 'Vendredi' : 'Friday'}: {officeHours('Friday')}
            </div>
            <div>
              {local === 'fr' ? 'Samedi' : 'Saturday'}:{' '}
              {officeHours('Saturday')}
            </div>
            <div>
              {local === 'fr' ? 'Dimanche' : 'Sunday'}: {officeHours('Sunday')}
            </div>
            <br />
          </div>

          <div
            onClick={() => setExpandGateHours(!expandGateHours)}
            style={{ paddingTop: '10px' }}
          >
            <b>
              {t.gateHours}{' '}
              <img
                src={chevronDown.src}
                style={{ transform: expandGateHours ? 'rotateX(180deg)' : '' }}
                alt="chevron-down"
              />{' '}
            </b>
          </div>
          <div
            style={{
              display: expandGateHours ? 'block' : 'none',
              paddingLeft: '10px',
            }}
          >
            <div>
              {local === 'fr' ? 'Lundi' : 'Monday'}: {gateHours('Monday')}
            </div>
            <div>
              {local === 'fr' ? 'Mardi' : 'Tuesday'}: {gateHours('Tuesday')}
            </div>
            <div>
              {local === 'fr' ? 'Mercredi' : 'Wednesday'}:{' '}
              {gateHours('Wednesday')}
            </div>
            <div>
              {local === 'fr' ? 'Jeudi' : 'Thursday'}: {gateHours('Thursday')}
            </div>
            <div>
              {local === 'fr' ? 'Vendredi' : 'Friday'}: {gateHours('Friday')}
            </div>
            <div>
              {local === 'fr' ? 'Samedi' : 'Saturday'}: {gateHours('Saturday')}
            </div>
            <div>
              {local === 'fr' ? 'Dimanche' : 'Sunday'}: {gateHours('Sunday')}
            </div>
            <br />
          </div>
        </div>
      </div>

      <hr />
      <br />
    </div>
  );
};

export default ClientHubContact;
